import React, {useCallback, useEffect, useMemo, useState} from 'react';
import usePageTitle from '~/app/hooks/usePageTitle';
import {useAppHeader} from '~/app/modules/appHeader';
import List from './List';
import useTranslation from "~/app/hooks/useTranslation";
import {useStoreActions, useStoreState} from '~/store';
import Header from './Header';
import Footer from './Footer';
import {useParams} from 'react-router-dom';
import {Project} from "~/types";
import {route} from "../app/helpers/route";
import {MACHINES_ROUTES} from "../machines/Router";
import EmptyMessage from './EmptyMessage'
import AddIssue, {AddIssueProps} from '~/issues/Add';
import Quantity from './Dialog/Quantity';
import Message from './Message';
import classes from './style.module.scss';
import moment from 'moment';
import {InspectionTypes} from "~/periodicCheck/store";
import {SOURCE_ISSUE_TYPE} from "~/app/data/statuses/issue";
import { PERIODIC_CHECK_ITEM_NOT_APPLICABLE } from '~/app/data/statuses/periodicCheck';
import { OperatingTypes } from '~/app/data/statuses/operatingData';
import {MACHINES_DETAILS_ROUTES} from "~/machines/Details/Router";
import { getPeriodicCheckQueueVersion, getQueuedPeriodicCheck, subscribePeriodicCheckQueue } from '~/data/sync/periodicCheckQueue';
import { Machine } from '~/types';

export type PeriodicCheckComponent = React.VFC;

const PeriodicCheck: PeriodicCheckComponent = () => {
    const {t} = useTranslation("periodicCheck");

    const {setData} = useAppHeader();
    const {projectId} = useParams<{ projectId: Project['id'] }>();
    const machine = useStoreState(state => state.machine.data);
    const checkEquipment = useStoreState(state => state.periodicCheck.checkEquipment);
    const headerSubTitle = useMemo(() => {
        if (checkEquipment?.id) {
            const selected =
                String(machine.id) === String(checkEquipment.id)
                    ? machine
                    : machine.linkedMachines?.find(l => String(l.id) === String(checkEquipment.id)) ?? checkEquipment;
            const line = [selected?.intern, selected?.name].filter(Boolean).join(' - ').toUpperCase();
            if (line) {
                return line;
            }
        }
        return [machine?.intern, machine?.name].filter(Boolean).join(' - ').toUpperCase();
    }, [checkEquipment, machine]);
    const updateCheckData = useStoreActions(actions => actions.periodicCheck.updateCheckData);
    const updateCurrentCheck = useStoreActions(actions => actions.periodicCheck.updateCurrentCheck);
    const checksData = useStoreState(state => state.periodicCheck.checksData);
    const inspectionType = useStoreState(state => state.periodicCheck.inspectionType);
    const sessionMode = useStoreState(state => state.periodicCheck.sessionMode);
    const openFromMachine = useStoreActions(actions => actions.periodicCheck.openFromMachine);
    const userId = useStoreState(state => state.user.data?.id);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const machineFetching = useStoreState(state => state.machine.fetching);
    const readOnly = sessionMode === 'submitted_pending';
    const [issueType, setIssueType] = useState<AddIssueProps['type']>('issue');
    const [openAddIssueDialog, setAddIssueDialog] = useState<boolean>(false);
    const [openQuantityDialog, setOpenQuantityDialog] = useState<boolean>(false);
    const [restoreAttempted, setRestoreAttempted] = useState(false);
    const [queueVersion, setQueueVersion] = useState(getPeriodicCheckQueueVersion);

    usePageTitle(t(`${inspectionType}.title`));

    useEffect(() => subscribePeriodicCheckQueue(setQueueVersion), []);

    useEffect(() => {
        setData({
            title: t(`${inspectionType}.title`),
            subTitle: headerSubTitle,
            backLink: inspectionType !== InspectionTypes.ACCEPTANCE_INSPECTION && machine.linkedMachines && machine.linkedMachines.length > 0
                ? route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, { id: machine.id, projectId })
                : route(MACHINES_ROUTES.details, { id: machine.id, projectId }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false
        });
    }, [setData, machine, headerSubTitle, t, projectId, inspectionType]);

    const resolveEquipment = useCallback((): Machine => {
        if (!checkEquipment?.id || String(checkEquipment.id) === String(machine.id)) {
            return machine;
        }
        return machine.linkedMachines?.find(linked => String(linked.id) === String(checkEquipment.id)) ?? machine;
    }, [checkEquipment, machine]);

    useEffect(() => {
        if (checksData.length || !machine.id || machineFetching) {
            return;
        }
        let cancelled = false;
        const equipment = resolveEquipment();
        const lastRegisteredCheck = inspectionType === InspectionTypes.ACCEPTANCE_INSPECTION
            ? equipment.lastAcceptanceInspection
            : equipment.lastDailyCheck;
        openFromMachine({
            equipment,
            projectId,
            inspectionType,
            lastRegisteredCheck,
        }).finally(() => {
            if (!cancelled) {
                setRestoreAttempted(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [checksData.length, inspectionType, machine.id, machineFetching, openFromMachine, projectId, resolveEquipment]);

    useEffect(() => {
        if (sessionMode !== 'submitted_pending' || netWorkStatus !== 0 || !machine.id || !userId) {
            return;
        }
        let cancelled = false;
        const equipment = resolveEquipment();
        const lastRegisteredCheck = inspectionType === InspectionTypes.ACCEPTANCE_INSPECTION
            ? equipment.lastAcceptanceInspection
            : equipment.lastDailyCheck;
        if (lastRegisteredCheck?.pendingSync) {
            return;
        }
        getQueuedPeriodicCheck(Number(userId), equipment.id, Number(inspectionType)).then(queued => {
            if (cancelled || (queued && !queued.isDraft)) {
                return;
            }
            openFromMachine({
                equipment,
                projectId,
                inspectionType,
                lastRegisteredCheck,
            });
        });
        return () => {
            cancelled = true;
        };
    }, [
        inspectionType,
        machine.id,
        machine.lastAcceptanceInspection,
        machine.lastDailyCheck,
        netWorkStatus,
        openFromMachine,
        projectId,
        resolveEquipment,
        sessionMode,
        userId,
        queueVersion,
    ]);

    const toggleQuantityDialog = useCallback(() => {
        openQuantityDialog && updateCurrentCheck(null);
        setOpenQuantityDialog(!openQuantityDialog);
    }, [setOpenQuantityDialog, openQuantityDialog, updateCurrentCheck]);

    const toggleAddIssueDialog = useCallback(() => {
        openAddIssueDialog && updateCurrentCheck(null);
        setAddIssueDialog(!openAddIssueDialog);
    }, [setAddIssueDialog, openAddIssueDialog, updateCurrentCheck]);

    const changeStatus = useCallback((check, status) => {
        updateCurrentCheck(check);
        const isNotApplicable = status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE
            || (status == null && check.status === PERIODIC_CHECK_ITEM_NOT_APPLICABLE);
        if (status === 2) {
            setIssueType('issue');
            toggleAddIssueDialog();
        } else if (check.fluidTypeId && !isNotApplicable) {
            toggleQuantityDialog();
        } else {
            check.status = status;
            check.quantity = 0;
            updateCheckData({...check});
            updateCurrentCheck(null);
        }
    }, [setIssueType, toggleAddIssueDialog, toggleQuantityDialog, updateCheckData, updateCurrentCheck]);

    const showRegisterOperatingTimeMessage = useMemo(() => {
        return [InspectionTypes.DAILY, InspectionTypes.PERIODIC].includes(inspectionType)
            && !moment(machine.lastOperatingTimeDate).isSame(moment(), 'day')
            && !!machine.operatingTimeAllowed
    }, [machine.lastOperatingTimeDate, machine.operatingTimeAllowed, inspectionType]);

    const showRegisterOperatingCountMessage = useMemo(() => {
        return [InspectionTypes.DAILY, InspectionTypes.PERIODIC].includes(inspectionType)
            && !moment(machine.lastOperatingCountDate).isSame(moment(), 'day')
            && !!machine.operatingCountAllowed
    }, [machine.operatingCountAllowed, machine.lastOperatingCountDate, inspectionType]);

    if (!checksData.length && !restoreAttempted) {
        return null;
    }

    if (!checksData.length) {
        return <EmptyMessage/>;
    }

    return (
        <>
            {showRegisterOperatingTimeMessage && <Message type={machine.categoryType} operatingUnitType={OperatingTypes.OPERATING_TIME} />}
            {showRegisterOperatingCountMessage && <Message type={machine.categoryType} operatingUnitType={OperatingTypes.OPERATING_COUNT} />}
            <div className={showRegisterOperatingTimeMessage || showRegisterOperatingCountMessage ? classes['disabled'] : ''}>
                <Header/>
                <List list={checksData} onSelect={readOnly ? undefined : changeStatus} readOnly={readOnly}/>
            </div>
            <Footer/>
            <AddIssue
                open={openAddIssueDialog}
                onClose={toggleAddIssueDialog}
                type={issueType}
                inspectionType={inspectionType}
                sourceTypeId={SOURCE_ISSUE_TYPE.DAILY_CHECK}
                allowAddMore={true}
            />
            <Quantity open={openQuantityDialog} onClose={toggleQuantityDialog}/>
        </>
    );
};

export default PeriodicCheck
